import { ChangeEventHandler, FormEventHandler, useContext, useState } from 'react'
import { UserDocumentTemplateId } from 'packages/types'
import { trackInternalEvent } from '@rio/tracking'
import Checkbox from 'builder/components/Checkbox'
import { TextField } from 'builder/components/TextField'
import { useI18n } from 'builder/hooks/useI18n'
import { validateEmail } from 'builder/components/SimpleEmailForm/utils/validateEmail'
import { apiClient } from 'builder/modules/apiClient'
import { navigate } from 'builder/modules/navigate'
import { ButtonTheme } from 'builder/components/Button'
import { useSignUpCopyright } from 'builder/components/SignUpCopyright'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import ErrorLogger from 'builder/services/ErrorLogger'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import { SectionNames, PrefillResumeOptions } from 'builder/modules/resumeEditor'
import ModalOverlay from '../ModalOverlay'
import { FILL_BY_URL_PARAM } from '../FillResumeModal/constants'
import emailIllustration from './assets/email-illustration.png'
import {
  Container,
  Form,
  Content,
  EmailContainer,
  Footer,
  Header,
  Copyright,
  Title,
  CloseButton,
  ConsentCheckboxWrapper,
  Modal,
  EmailImage,
  StyledButton,
} from './styles'

type Props = {
  templateId: UserDocumentTemplateId
  prefillOption: PrefillResumeOptions
  onClose: () => void
}

export const SignUpBuilderGuestModal = (props: Props) => {
  const { onClose, prefillOption } = props
  const { i18n } = useI18n()
  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [email, setEmail] = useState('')
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const copyrightMarkup = useSignUpCopyright({ isEmailOnly: true })
  const { isPhone } = useMediaQueries()
  const { resetResume, resume } = useContext(GuestResumeContext)

  useEffectOnMount(() => {
    trackInternalEvent('show_sign_up_banner', { label: 'general' })
  })

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = event => {
    setEmailErrorMessage('')
    setEmail(event.target.value)
  }

  const onSignUpSubmit: FormEventHandler = async event => {
    trackInternalEvent('click_create_resume', { label: 'sign_up_banner' })
    event.preventDefault()

    if (!email) {
      setEmailErrorMessage(i18n.t('builder.sign_up.field_is_required'))
      return
    }

    setIsSubmitting(true)

    try {
      const { message } = await validateEmail(email, { allowAvailableOnly: true })

      if (message) {
        setIsSubmitting(false)
        setEmailErrorMessage(message)
        return
      }

      const { data } = await apiClient.post(`/sign-up/register`, {
        signUp: {
          email,
          confirmEmail: isConsentChecked,
          showPrefillResumeModal: false,
          template: resume.template,
          resumeData: { stepByStepFlow: { currentStep: SectionNames.details } },
        },
      })

      if (data.success) {
        const params = new URLSearchParams()
        params.append(FILL_BY_URL_PARAM, prefillOption)
        setIsSubmitting(false)
        resetResume()
        navigate(`/resumes/${data.firstDocumentId}/edit?${params}`, {
          replace: true,
        })
      }
    } catch (error) {
      ErrorLogger.log(error)
      setIsSubmitting(false)
    }
  }

  return (
    <ModalOverlay
      overlayFadeDuration={300}
      contentSlideDuration={300}
      onClick={onClose}
      fullScreen={isPhone}
    >
      <Modal>
        <Container>
          <Form onSubmit={onSignUpSubmit}>
            <Content>
              <Header>
                <EmailImage src={emailIllustration} alt="Email illustration" />
                <Title>{i18n.t('builder.sign_up.sign_up_guest_modal.title')}</Title>
              </Header>

              <EmailContainer>
                <TextField
                  label={i18n.t('builder.sign_up.email')}
                  type="email"
                  value={email || ''}
                  name="email"
                  onChange={onEmailChange}
                  error={emailErrorMessage}
                />
                <ConsentCheckboxWrapper>
                  <Checkbox
                    label={i18n.t('builder.sign_up.consent_checkbox_label')}
                    checked={isConsentChecked}
                    onChange={event => setIsConsentChecked(event.target.checked)}
                    name="confirm_email"
                    align="top"
                  />
                </ConsentCheckboxWrapper>
              </EmailContainer>

              <Copyright dangerouslySetInnerHTML={{ __html: copyrightMarkup }} />
            </Content>

            <Footer>
              <StyledButton type="button" onClick={onClose} theme={ButtonTheme.ghost}>
                {i18n.t('builder.sign_up.sign_up_guest_modal.back_button_text')}
              </StyledButton>
              <StyledButton type="submit" isLoading={isSubmitting} isDisabled={isSubmitting}>
                {i18n.t('builder.sign_up.sign_up_guest_modal.button_text')}
              </StyledButton>
            </Footer>
          </Form>
        </Container>
        <CloseButton onClick={onClose} />
      </Modal>
    </ModalOverlay>
  )
}
